import types from '../../mutation-types';
import ConversationApi from '../../../api/inbox/conversation';
import MessageApi from '../../../api/inbox/message';
import { MESSAGE_STATUS, MESSAGE_TYPE } from 'shared/constants/messages';
import { createPendingMessage } from 'dashboard/helper/commons';
import {
  buildConversationList,
  isOnMentionsView,
  isOnUnattendedView,
  isOnFoldersView,
} from './helpers/actionHelpers';
import messageReadActions from './actions/messageReadActions';
import messageTranslateActions from './actions/messageTranslateActions';
import * as Sentry from '@sentry/vue';
import {
  handleVoiceCallCreated,
  handleVoiceCallUpdated,
} from 'dashboard/helper/voice';

export const hasMessageFailedWithExternalError = pendingMessage => {
  // This helper is used to check if the message has failed with an external error.
  // We have two cases
  // 1. Messages that fail from the UI itself (due to large attachments or a failed network):
  //    In this case, the message will have a status of failed but no external error. So we need to create that message again
  // 2. Messages sent from Chatwoot but failed to deliver to the customer for some reason (user blocking or client system down):
  //    In this case, the message will have a status of failed and an external error. So we need to retry that message
  const { content_attributes: contentAttributes, status } = pendingMessage;
  const externalError = contentAttributes?.external_error ?? '';
  return status === MESSAGE_STATUS.FAILED && externalError !== '';
};

export const findConfirmedMessageEcho = (state, pendingMessage) => {
  // Match only a server identity for this exact local attempt. Content and
  // timestamps cannot prove that a message was accepted.
  if (!pendingMessage.echo_id || Number.isInteger(pendingMessage.id)) {
    return undefined;
  }
  const conversation = state?.allConversations?.find(
    item => item.id === pendingMessage.conversation_id
  );
  return conversation?.messages?.find(
    item => Number.isInteger(item.id) && item.echo_id === pendingMessage.echo_id
  );
};

// Each store state owns its recovery generation. Cancelling a scope invalidates
// late responses, including a route/account A -> B -> A transition.
const conversationRecoveries = new WeakMap();
const conversationListEpochs = new WeakMap();
const conversationListLoadingTokens = new WeakMap();
// Exclude message buffers; they are always retained when merging recovery data.
const recoveryEntrySnapshot = row =>
  JSON.parse(
    JSON.stringify({
      ...row,
      messages: undefined,
      dataFetched: undefined,
      allMessagesLoaded: undefined,
    })
  );
const isRecoveryObject = value =>
  value !== null && typeof value === 'object' && !Array.isArray(value);
// Preserve only fields changed locally during this GET. An unread event must
// not hide an independent server assignment, including equal-second updates.
const mergeRecoveryFields = (before, local, remote, newerServer) =>
  Object.entries(remote).reduce(
    (merged, [key, value]) => {
      const previous = before[key];
      const current = local[key];
      if (
        isRecoveryObject(previous) &&
        isRecoveryObject(current) &&
        isRecoveryObject(value)
      ) {
        // A changed identity is atomic; never combine one owner's ID with
        // another owner's name or details.
        if (current.id !== previous.id) {
          if (newerServer) merged[key] = value;
          return merged;
        }
        merged[key] =
          value.id !== previous.id
            ? value
            : mergeRecoveryFields(previous, current, value, newerServer);
      } else if (JSON.stringify(previous) === JSON.stringify(current)) {
        merged[key] = value;
      }
      return merged;
    },
    { ...local }
  );
const listScope = filters =>
  JSON.stringify([
    filters.assigneeType,
    filters.inboxId,
    filters.status,
    filters.sortBy,
    filters.labels || [],
    filters.teamId,
    filters.conversationType,
  ]);

// actions
const actions = {
  getConversation: async ({ commit }, conversationId) => {
    try {
      const response = await ConversationApi.show(conversationId);
      commit(types.UPDATE_CONVERSATION, response.data);
      commit(`contacts/${types.SET_CONTACT_ITEM}`, response.data.meta.sender);
    } catch (error) {
      // Ignore error
    }
  },

  fetchAllConversations: async ({ commit, state, dispatch, rootGetters }) => {
    const params = JSON.parse(JSON.stringify(state.conversationFilters));
    const scope = listScope(params);
    const accountId = rootGetters?.getCurrentAccountId;
    const userId = rootGetters?.getCurrentUser?.id;
    const epoch = conversationListEpochs.get(state);
    const loadingToken = {};
    const current = () =>
      listScope(state.conversationFilters) === scope &&
      rootGetters?.getCurrentAccountId === accountId &&
      rootGetters?.getCurrentUser?.id === userId &&
      conversationListEpochs.get(state) === epoch &&
      conversationListLoadingTokens.get(state) === loadingToken;
    conversationListLoadingTokens.set(state, loadingToken);
    commit(types.SET_LIST_LOADING_STATUS);
    try {
      const {
        data: { data },
      } = await ConversationApi.get(params);
      if (!current()) return false;
      buildConversationList(
        { commit, dispatch },
        params,
        data,
        params.assigneeType
      );
      return true;
    } catch (error) {
      if (current()) commit(types.SET_LIST_LOAD_FAILED);
      return false;
    } finally {
      if (conversationListLoadingTokens.get(state) === loadingToken) {
        commit(types.CLEAR_LIST_LOADING_STATUS);
        conversationListLoadingTokens.delete(state);
      }
    }
  },

  fetchFilteredConversations: async (
    { commit, dispatch, state, rootGetters },
    params
  ) => {
    const request = JSON.parse(JSON.stringify(params));
    const accountId = rootGetters?.getCurrentAccountId;
    const userId = rootGetters?.getCurrentUser?.id;
    const epoch = state && conversationListEpochs.get(state);
    const filterContext = () =>
      JSON.stringify([
        state?.appliedFilters,
        rootGetters?.getAppliedConversationFiltersQuery,
        rootGetters?.['customViews/getActiveConversationFolder'],
      ]);
    const scope = filterContext();
    const loadingToken = {};
    const current = () =>
      rootGetters?.getCurrentAccountId === accountId &&
      rootGetters?.getCurrentUser?.id === userId &&
      filterContext() === scope &&
      (!state ||
        (conversationListEpochs.get(state) === epoch &&
          conversationListLoadingTokens.get(state) === loadingToken));
    if (state) conversationListLoadingTokens.set(state, loadingToken);
    commit(types.SET_LIST_LOADING_STATUS);
    try {
      const { data } = await ConversationApi.filter(request);
      if (!current()) return false;
      buildConversationList(
        { commit, dispatch },
        request,
        data,
        'appliedFilters'
      );
      return true;
    } catch (error) {
      if (current()) commit(types.SET_LIST_LOAD_FAILED);
      return false;
    } finally {
      if (!state || conversationListLoadingTokens.get(state) === loadingToken) {
        commit(types.CLEAR_LIST_LOADING_STATUS);
        if (state) conversationListLoadingTokens.delete(state);
      }
    }
  },

  cancelConversationListRecovery({ state }) {
    conversationRecoveries.delete(state);
  },

  recoverConversationList: async (
    { commit, dispatch, state, rootGetters },
    { filters, accountId, userId }
  ) => {
    const scope = listScope(filters);
    const token = {};
    const current = () =>
      conversationRecoveries.get(state) === token &&
      rootGetters.getCurrentAccountId === accountId &&
      rootGetters.getCurrentUser?.id === userId &&
      listScope(state.conversationFilters) === scope &&
      !state.appliedFilters?.length;
    if (filters.assigneeType !== 'me' || filters.conversationType) {
      return { ok: false, stale: true };
    }
    conversationRecoveries.set(state, token);
    if (!current()) return { ok: false, stale: true };
    const params = JSON.parse(JSON.stringify(filters));
    delete params.updatedWithin;
    const seen = new Set();
    let targetCount;
    let page = 1;
    try {
      while (current()) {
        const beforeRequest = new Map(
          state.allConversations.map(row => [
            row.id,
            recoveryEntrySnapshot(row),
          ])
        );
        const {
          data: { data },
        } = await ConversationApi.get({ ...params, page }); // eslint-disable-line no-await-in-loop -- Each page requires the prior count and scope check.
        if (!current()) return { ok: false, stale: true };
        const { payload, meta } = data;
        if (
          !Array.isArray(payload) ||
          !Number.isFinite(Number(meta.mine_count))
        ) {
          return { ok: false };
        }
        targetCount ??= Number(meta.mine_count);
        const previousSize = seen.size;
        const updates = payload.map(conversation => {
          seen.add(conversation.id);
          const existing = state.allConversations.find(
            row => row.id === conversation.id
          );
          if (!existing) return conversation;
          if (
            conversation.updated_at < existing.updated_at ||
            !beforeRequest.has(existing.id)
          )
            return existing;
          return {
            ...mergeRecoveryFields(
              beforeRequest.get(existing.id),
              existing,
              conversation,
              conversation.updated_at > existing.updated_at
            ),
            messages: existing.messages,
            dataFetched: existing.dataFetched,
            allMessagesLoaded: existing.allMessagesLoaded,
          };
        });
        commit(types.SET_ALL_CONVERSATION, updates);
        commit(
          `contacts/${types.SET_CONTACTS}`,
          updates.map(row => row.meta.sender)
        );
        dispatch('conversationLabels/setBulkConversationLabels', updates);
        dispatch('conversationStats/set', meta);
        if (!payload.length || seen.size >= targetCount) {
          return { ok: true, complete: true, received: seen.size };
        }
        if (seen.size === previousSize)
          return { ok: false, received: seen.size };
        page += 1;
      }
      return { ok: false, stale: true };
    } catch {
      return { ok: false };
    } finally {
      if (conversationRecoveries.get(state) === token) {
        conversationRecoveries.delete(state);
      }
    }
  },

  emptyAllConversations({ commit, state }) {
    if (state) {
      conversationListEpochs.set(state, {});
      conversationRecoveries.delete(state);
    }
    commit(types.EMPTY_ALL_CONVERSATION);
  },

  clearSelectedState({ commit }) {
    commit(types.CLEAR_CURRENT_CHAT_WINDOW);
  },

  fetchPreviousMessages: async ({ commit }, data) => {
    try {
      const {
        data: { meta, payload },
      } = await MessageApi.getPreviousMessages(data);
      commit(`conversationMetadata/${types.SET_CONVERSATION_METADATA}`, {
        id: data.conversationId,
        data: meta,
      });
      commit(types.SET_PREVIOUS_CONVERSATIONS, {
        id: data.conversationId,
        data: payload,
      });
      if (!payload.length) {
        commit(types.SET_ALL_MESSAGES_LOADED, data.conversationId);
      }
    } catch (error) {
      // Handle error
    }
  },

  fetchAllAttachments: async ({ commit }, conversationId) => {
    let attachments = [];

    try {
      const { data } = await ConversationApi.getAllAttachments(conversationId);
      attachments = data.payload;
    } catch (error) {
      // in case of error, log the error and continue
      Sentry.setContext('Conversation', {
        id: conversationId,
      });
      Sentry.captureException(error);
    } finally {
      // we run the commit even if the request fails
      // this ensures that the `attachment` variable is always present on chat
      commit(types.SET_ALL_ATTACHMENTS, {
        id: conversationId,
        data: attachments,
      });
    }
  },

  syncActiveConversationMessages: async (
    { commit, state, dispatch },
    { conversationId }
  ) => {
    const { allConversations, syncConversationsMessages } = state;
    const lastMessageId = syncConversationsMessages[conversationId];
    const selectedChat = allConversations.find(
      conversation => conversation.id === conversationId
    );
    if (!selectedChat) return;
    try {
      const { messages } = selectedChat;
      // Fetch all the messages after the last message id
      const {
        data: { meta, payload },
      } = await MessageApi.getPreviousMessages({
        conversationId,
        after: lastMessageId,
      });
      commit(`conversationMetadata/${types.SET_CONVERSATION_METADATA}`, {
        id: conversationId,
        data: meta,
      });
      // Find the messages that are not already present in the store
      const missingMessages = payload.filter(
        message => !messages.find(item => item.id === message.id)
      );
      selectedChat.messages.push(...missingMessages);
      // Sort the messages by created_at
      const sortedMessages = selectedChat.messages.sort((a, b) => {
        return new Date(a.created_at) - new Date(b.created_at);
      });
      commit(types.SET_MISSING_MESSAGES, {
        id: conversationId,
        data: sortedMessages,
      });
      commit(types.SET_LAST_MESSAGE_ID_IN_SYNC_CONVERSATION, {
        conversationId,
        messageId: null,
      });
      dispatch('markMessagesRead', { id: conversationId }, { root: true });
    } catch (error) {
      // Handle error
    }
  },

  setConversationLastMessageId: async (
    { commit, state },
    { conversationId }
  ) => {
    const { allConversations } = state;
    const selectedChat = allConversations.find(
      conversation => conversation.id === conversationId
    );
    if (!selectedChat) return;
    const { messages } = selectedChat;
    const lastMessage = messages.last();
    if (!lastMessage) return;
    commit(types.SET_LAST_MESSAGE_ID_IN_SYNC_CONVERSATION, {
      conversationId,
      messageId: lastMessage.id,
    });
  },

  async setActiveChat({ commit, dispatch }, { data, after }) {
    commit(types.SET_CURRENT_CHAT_WINDOW, data);
    commit(types.CLEAR_ALL_MESSAGES_LOADED, data.id);
    if (data.dataFetched === undefined) {
      try {
        await dispatch('fetchPreviousMessages', {
          after,
          before: data.messages[0].id,
          conversationId: data.id,
        });
        commit(types.SET_CHAT_DATA_FETCHED, data.id);
      } catch (error) {
        // Ignore error
      }
    }
  },

  assignAgent: async ({ dispatch }, { conversationId, agentId }) => {
    try {
      const response = await ConversationApi.assignAgent({
        conversationId,
        agentId,
      });
      dispatch('setCurrentChatAssignee', {
        conversationId,
        assignee: response.data,
      });
    } catch (error) {
      // Handle error
    }
  },

  setCurrentChatAssignee({ commit }, { conversationId, assignee }) {
    commit(types.ASSIGN_AGENT, { conversationId, assignee });
  },

  assignTeam: async ({ dispatch }, { conversationId, teamId }) => {
    try {
      const response = await ConversationApi.assignTeam({
        conversationId,
        teamId,
      });
      dispatch('setCurrentChatTeam', { team: response.data, conversationId });
    } catch (error) {
      // Handle error
    }
  },

  setCurrentChatTeam({ commit }, { team, conversationId }) {
    commit(types.ASSIGN_TEAM, { team, conversationId });
  },

  toggleStatus: async (
    { commit },
    { conversationId, status, snoozedUntil = null, customAttributes = null }
  ) => {
    try {
      // Update custom attributes first if provided
      if (customAttributes) {
        await ConversationApi.updateCustomAttributes({
          conversationId,
          customAttributes,
        });
        commit(types.UPDATE_CONVERSATION_CUSTOM_ATTRIBUTES, {
          conversationId,
          customAttributes,
        });
      }

      const {
        data: {
          payload: {
            current_status: updatedStatus,
            snoozed_until: updatedSnoozedUntil,
          } = {},
        } = {},
      } = await ConversationApi.toggleStatus({
        conversationId,
        status,
        snoozedUntil,
      });
      commit(types.CHANGE_CONVERSATION_STATUS, {
        conversationId,
        status: updatedStatus,
        snoozedUntil: updatedSnoozedUntil,
      });
    } catch (error) {
      // Handle error
    }
  },

  createPendingMessageAndSend: async ({ dispatch }, data) => {
    const pendingMessage = createPendingMessage(data);
    return dispatch('sendMessageWithData', pendingMessage);
  },

  sendMessageWithData: async ({ commit, state }, pendingMessage) => {
    const { conversation_id: conversationId, id } = pendingMessage;
    const confirmedMessage = findConfirmedMessageEcho(state, pendingMessage);
    if (confirmedMessage) return confirmedMessage;
    try {
      commit(types.ADD_MESSAGE, {
        ...pendingMessage,
        status: MESSAGE_STATUS.PROGRESS,
      });
      const response = hasMessageFailedWithExternalError(pendingMessage)
        ? await MessageApi.retry(conversationId, id)
        : await MessageApi.create(pendingMessage);
      commit(types.ADD_MESSAGE, {
        ...response.data,
        status: MESSAGE_STATUS.SENT,
      });
      commit(types.ADD_CONVERSATION_ATTACHMENTS, {
        ...response.data,
        status: MESSAGE_STATUS.SENT,
      });
      return response.data;
    } catch (error) {
      // A realtime echo may have arrived before a lost HTTP response. Never
      // replace that confirmation with a retryable local failure.
      const confirmedEcho = findConfirmedMessageEcho(state, pendingMessage);
      if (confirmedEcho) return confirmedEcho;
      const errorMessage = error.response
        ? error.response.data.error
        : undefined;
      commit(types.ADD_MESSAGE, {
        ...pendingMessage,
        meta: {
          error: errorMessage,
        },
        status: MESSAGE_STATUS.FAILED,
      });
      throw error;
    }
  },

  addMessage({ commit, rootGetters }, message) {
    commit(types.ADD_MESSAGE, message);
    if (message.message_type === MESSAGE_TYPE.INCOMING) {
      commit(types.SET_CONVERSATION_CAN_REPLY, {
        conversationId: message.conversation_id,
        canReply: true,
      });
      commit(types.ADD_CONVERSATION_ATTACHMENTS, message);
    }
    handleVoiceCallCreated(message, rootGetters?.getCurrentUserID);
  },

  updateMessage({ commit, rootGetters }, message) {
    commit(types.ADD_MESSAGE, message);
    handleVoiceCallUpdated(commit, message, rootGetters?.getCurrentUserID);
  },

  deleteMessage: async function deleteLabels(
    { commit },
    { conversationId, messageId }
  ) {
    try {
      const { data } = await MessageApi.delete(conversationId, messageId);
      commit(types.ADD_MESSAGE, data);
      commit(types.DELETE_CONVERSATION_ATTACHMENTS, data);
    } catch (error) {
      throw new Error(error);
    }
  },

  deleteConversation: async ({ commit, dispatch }, conversationId) => {
    try {
      await ConversationApi.delete(conversationId);
      commit(types.DELETE_CONVERSATION, conversationId);
      dispatch('conversationStats/get', {}, { root: true });
    } catch (error) {
      throw new Error(error);
    }
  },

  addConversation({ commit, state, dispatch, rootState }, conversation) {
    const { currentInbox, appliedFilters } = state;
    const {
      inbox_id: inboxId,
      meta: { sender },
    } = conversation;
    const hasAppliedFilters = !!appliedFilters.length;
    const isMatchingInboxFilter =
      !currentInbox || Number(currentInbox) === inboxId;
    if (
      !hasAppliedFilters &&
      !isOnFoldersView(rootState) &&
      !isOnMentionsView(rootState) &&
      !isOnUnattendedView(rootState) &&
      isMatchingInboxFilter
    ) {
      commit(types.ADD_CONVERSATION, conversation);
      dispatch('contacts/setContact', sender);
    }
  },

  addMentions({ dispatch, rootState }, conversation) {
    if (isOnMentionsView(rootState)) {
      dispatch('updateConversation', conversation);
    }
  },

  addUnattended({ dispatch, rootState }, conversation) {
    if (isOnUnattendedView(rootState)) {
      dispatch('updateConversation', conversation);
    }
  },

  updateConversation({ commit, dispatch }, conversation) {
    const {
      meta: { sender },
    } = conversation;
    commit(types.UPDATE_CONVERSATION, conversation);

    dispatch('conversationLabels/setConversationLabel', {
      id: conversation.id,
      data: conversation.labels,
    });

    dispatch('contacts/setContact', sender);
  },

  updateConversationLastActivity(
    { commit },
    { conversationId, lastActivityAt }
  ) {
    commit(types.UPDATE_CONVERSATION_LAST_ACTIVITY, {
      lastActivityAt,
      conversationId,
    });
  },

  setChatStatusFilter({ commit }, data) {
    commit(types.CHANGE_CHAT_STATUS_FILTER, data);
  },

  setChatSortFilter({ commit }, data) {
    commit(types.CHANGE_CHAT_SORT_FILTER, data);
  },

  updateAssignee({ commit }, data) {
    commit(types.UPDATE_ASSIGNEE, data);
  },

  updateConversationContact({ commit }, data) {
    if (data.id) {
      commit(`contacts/${types.SET_CONTACT_ITEM}`, data);
    }
    commit(types.UPDATE_CONVERSATION_CONTACT, data);
  },

  setActiveInbox({ commit }, inboxId) {
    commit(types.SET_ACTIVE_INBOX, inboxId);
  },

  muteConversation: async ({ commit }, conversationId) => {
    try {
      await ConversationApi.mute(conversationId);
      commit(types.MUTE_CONVERSATION);
    } catch (error) {
      //
    }
  },

  unmuteConversation: async ({ commit }, conversationId) => {
    try {
      await ConversationApi.unmute(conversationId);
      commit(types.UNMUTE_CONVERSATION);
    } catch (error) {
      //
    }
  },

  sendEmailTranscript: async (_, { conversationId, email }) => {
    await ConversationApi.sendEmailTranscript({ conversationId, email });
  },

  updateCustomAttributes: async (
    { commit },
    { conversationId, customAttributes }
  ) => {
    try {
      const response = await ConversationApi.updateCustomAttributes({
        conversationId,
        customAttributes,
      });
      const { custom_attributes } = response.data;
      commit(types.UPDATE_CONVERSATION_CUSTOM_ATTRIBUTES, {
        conversationId,
        customAttributes: custom_attributes,
      });
    } catch (error) {
      // Handle error
    }
  },

  setConversationFilters({ commit }, data) {
    commit(types.SET_CONVERSATION_FILTERS, data);
  },

  clearConversationFilters({ commit }) {
    commit(types.CLEAR_CONVERSATION_FILTERS);
  },

  setChatListFilters({ commit }, data) {
    commit(types.SET_CHAT_LIST_FILTERS, data);
  },

  updateChatListFilters({ commit }, data) {
    commit(types.UPDATE_CHAT_LIST_FILTERS, data);
  },

  assignPriority: async ({ dispatch }, { conversationId, priority }) => {
    try {
      await ConversationApi.togglePriority({
        conversationId,
        priority,
      });

      dispatch('setCurrentChatPriority', {
        priority,
        conversationId,
      });
    } catch (error) {
      // Handle error
    }
  },

  setCurrentChatPriority({ commit }, { priority, conversationId }) {
    commit(types.ASSIGN_PRIORITY, { priority, conversationId });
  },

  setContextMenuChatId({ commit }, chatId) {
    commit(types.SET_CONTEXT_MENU_CHAT_ID, chatId);
  },

  getInboxCaptainAssistantById: async ({ commit }, conversationId) => {
    try {
      const response = await ConversationApi.getInboxAssistant(conversationId);
      commit(types.SET_INBOX_CAPTAIN_ASSISTANT, response.data);
    } catch (error) {
      // Handle error
    }
  },

  ...messageReadActions,
  ...messageTranslateActions,
};

export default actions;
